import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { Files, Plus, X, Save, Trash2, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailTemplatesPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const load = async () => { const { data } = await api.get('/email/templates'); setItems(data.templates); };
  useEffect(() => { load(); }, []);
  const newTpl = () => setEditing({ name: '', subject: 'Hola (nombre_cliente)', body_html: '<p>Hola (nombre_cliente),</p><p>Soy (nombre_empleado) de (nombre_empresa).</p>', enabled: true, category: 'general' });
  const save = async () => {
    try {
      if (editing.id) await api.patch(`/email/templates/${editing.id}`, editing);
      else await api.post('/email/templates', editing);
      toast.success('Guardado'); setEditing(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };
  const del = async (t) => { if (!confirm('Borrar?')) return; await api.delete(`/email/templates/${t.id}`); load(); };
  const sendTest = async (t) => {
    const to = prompt('Enviar email de prueba a:', '');
    if (!to) return;
    try {
      const { data } = await api.post('/email/send', { to_email: to, subject: t.subject, body_html: t.body_html, template_id: t.id });
      if (data.result.sent) toast.success('Email enviado');
      else toast.error('No se pudo enviar', { description: data.result.reason });
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const categories = Array.from(new Set(items.map((t) => t.category || 'general'))).sort();
  const visible = filter ? items.filter((t) => (t.category || 'general') === filter) : items;

  return (
    <div>
      <PageHeader title="Plantillas Email" description="Crea plantillas con la categoría que quieras. Se pueden disparar desde lead detail, automatizaciones, invitaciones, etc." actions={<button data-testid="emailtpl-new" onClick={newTpl} className="btn-primary"><Plus className="h-4 w-4" /> Nueva plantilla</button>} />

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          <button onClick={() => setFilter('')} className={`chip hover:bg-accent ${!filter ? 'bg-accent text-foreground' : ''}`}>Todas</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`chip hover:bg-accent ${filter === c ? 'bg-accent text-foreground' : ''}`}>{c}</button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <EmptyState icon={Files} title="Sin plantillas" description="Crea plantillas para cualquier propósito: bienvenida, seguimiento, recordatorio, invitación, etc." cta={<button onClick={newTpl} className="btn-primary">Nueva</button>} />
      ) : (
        <div className="grid lg:grid-cols-2 gap-3">
          {visible.map((t) => (
            <div key={t.id} data-testid="emailtpl-card" className="surface p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-muted-foreground">Categoría: <span className="chip">{t.category || 'general'}</span> {t.enabled ? '' : '— inactiva'}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t.subject}</div>
                </div>
                <div className="flex gap-1"><button onClick={() => sendTest(t)} className="btn-ghost h-8 px-2 text-xs"><Send className="h-3 w-3" /> Probar</button><button onClick={() => setEditing(t)} className="btn-ghost h-8 px-2 text-xs">Editar</button><button onClick={() => del(t)} className="btn-ghost h-8 w-8 p-0 text-[hsl(var(--danger))]"><Trash2 className="h-4 w-4" /></button></div>
              </div>
              <div className="mt-3 bg-secondary/40 rounded-[10px] p-3 max-h-40 overflow-y-auto text-xs" dangerouslySetInnerHTML={{ __html: t.body_html }} />
              {(t.variables || []).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">{t.variables.map((v) => <span key={v} className="chip">{v}</span>)}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4"><h2 className="font-display text-xl font-semibold">{editing.id ? 'Editar' : 'Nueva'} plantilla</h2><button onClick={() => setEditing(null)} className="btn-ghost h-8 w-8 p-0"><X className="h-4 w-4" /></button></div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="space-y-3">
                <label>Nombre<input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="input mt-1" /></label>
                <label>Categoría (libre: invitation, bienvenida, recordatorio, lo que quieras)
                  <input list="email-cat-list" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="input mt-1" />
                  <datalist id="email-cat-list">
                    {['general', 'invitation', 'welcome', 'follow_up', 'reminder', 'closing', 'marketing', 'recordatorio', 'cotizacion', 'recuperacion'].map((c) => <option key={c} value={c} />)}
                  </datalist>
                </label>
                <label>Asunto<input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className="input mt-1" /></label>
                <label>HTML<textarea value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} className="input min-h-[260px] mt-1 font-mono text-xs" /></label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} /> Activa</label>
              </div>
              <div>
                <h3 className="text-sm font-medium mb-2">Vista previa</h3>
                <div className="rounded-[12px] border border-border bg-white text-black p-4 min-h-[260px] text-sm overflow-auto" dangerouslySetInnerHTML={{ __html: editing.body_html }} />
                <div className="mt-3 text-xs text-muted-foreground">
                  <p className="font-medium">Variables disponibles:</p>
                  <p>(nombre_cliente), (nombre_empleado), (nombre_empresa), (telefono_cliente), (ciudad), (servicio), (gestor_asignado), (invite_link) y cualquier campo personalizado.</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4"><button onClick={() => setEditing(null)} className="btn-ghost">Cancelar</button><button data-testid="emailtpl-save" onClick={save} className="btn-primary"><Save className="h-4 w-4" /> Guardar</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
